import { AbstractUser, Hostname } from "@kvix/shared";
import { Text } from "@kvix/ui";
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Facebook } from "@material-ui/icons";
import Apple from "@material-ui/icons/Apple";
import queryString from "query-string";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useTosVersion } from "../../../../cms/hooks/Terms";
import {
  getProvider,
  isBasicInstructor,
  KvixUserContext,
} from "../../../../contexts/user";

const useStyles = makeStyles((theme) => ({
  content: {
    textAlign: "center",
  },
  registerButton: {
    color: theme.palette.common.white,
    "&.apple": {
      color: "#4285F4",
    },
  },
  registerButtonBase: {
    marginBottom: "25px",
    width: "255px",
    height: 40,
    borderRadius: 20,
    position: "relative",
    "&.facebook": {
      backgroundColor: "#3A5898",
    },
    "&.google": {
      backgroundColor: "#4285F4",
    },
    "&.apple": {
      backgroundColor: theme.palette.common.white,
    },
  },
  brand: {
    fontSize: 14,
    fontWeight: "bold",
    letterSpacing: "0.25px",
    lineHeight: "20px",
    paddingRight: "12px",
    marginTop: "-1px",
    color: "inherit",
  },
  logotype: {
    position: "absolute",
    display: "flex",
    right: 14,

    "& img": {
      height: 24,
      width: 24,
    },
  },
  registerButtonContainer: {
    display: "block",
    justifyContent: "space-between",
    paddingBottom: "33px",
    paddingTop: "20px",
  },
  dialogRegisterButton: {
    display: "flex",
    flexDirection: "column",
    paddingBottom: "33px",
    paddingTop: "20px",
  },
  kvixButtonBase: {
    width: "255px",
    height: 40,
    borderRadius: 20,
    position: "relative",
    backgroundColor: theme.palette.primary.main,
    [theme.breakpoints.down("sm")]: {
      marginBottom: "25px",
    },
  },
  tosText: {
    marginBottom: 24,
    width: "100%",
    display: "flex",
    justifyContent: "center",
    "& p": {
      fontWeight: 600,
      maxWidth: 320,
      fontSize: "10px",
      lineHeight: "14px",
      color: theme.palette.common.white,
    },
  },
  registered: {
    marginBottom: 40,
  },
  dialogTitle: {
    marginTop: theme.spacing(4),
  },
}));

const Logo: React.FC<{ alt: string; src: string }> = (props) => {
  const { alt, src } = props;
  const classes = useStyles();

  return (
    <div className={classes.logotype}>
      <img alt={alt} src={src} />
    </div>
  );
};

const getRegisterUrl = (
  provider: "google" | "apple" | "facebook",
  tosVersion?: number
): string => {
  const host = Hostname.auth.register();
  return `${host}/auth/${provider}/register?redirectPath=/?${encodeURIComponent(
    queryString.stringify(
      { instructor: true, tosVersion },
      {
        skipNull: true,
      }
    )
  )}`;
};

const GoogleLogo = () => <Logo alt="google" src="/assets/brands/google.svg" />;

const RegisterButton: React.FC<{
  logo: JSX.Element;
  provider: "google" | "apple" | "facebook";
}> = ({ logo, provider }) => {
  const tosVersion = useTosVersion();
  const classes = useStyles();
  const { t, ready } = useTranslation("signUp");
  const buttonBaseProps = {
    href: getRegisterUrl(provider, tosVersion),
  };

  if (!ready) {
    return null;
  }

  return (
    <Box className={`${classes.registerButton} ${provider}`}>
      <ButtonBase
        {...buttonBaseProps}
        className={`${classes.registerButtonBase} ${provider}`}
      >
        {logo}
        <Text className={classes.brand}>{t("signUpUsing")}</Text>
      </ButtonBase>
    </Box>
  );
};

const RegisterDialogButton: React.FC<{ user: AbstractUser }> = ({ user }) => {
  const tosVersion = useTosVersion();
  const classes = useStyles();
  const { t, ready } = useTranslation("signUp");
  const provider = getProvider(user);
  const buttonBaseProps = {
    href: getRegisterUrl(provider, tosVersion),
  };

  if (!ready) {
    return null;
  }

  return (
    <Box className={`${classes.registerButton} ${provider}`}>
      <Button {...buttonBaseProps} className={`${classes.kvixButtonBase}`}>
        <Text className={classes.brand}>{t("registerBtn")}</Text>
      </Button>
    </Box>
  );
};

export const RegisterDialog: React.FC<{
  open: boolean;
  setOpen: (open: boolean) => void;
}> = ({ open, setOpen }) => {
  const classes = useStyles();
  const { user } = useContext(KvixUserContext);
  const isRegistered = isBasicInstructor(user);
  const { t, ready } = useTranslation("signUp");

  if (!ready) {
    return null;
  }

  return (
    <Dialog open={open} onClose={() => {}} className={classes.content}>
      <DialogTitle className={classes.dialogTitle}>
        {isRegistered ? (
          <Text variant="h2" color="primary" className={classes.registered}>
            {t("registered")}
          </Text>
        ) : (
          <Text variant="h2" color="primary">
            {t("register")}
          </Text>
        )}
      </DialogTitle>
      <DialogContent>
        {!user ? (
          <>
            <Box className={classes.registerButtonContainer}>
              <RegisterButton
                logo={<Facebook className={classes.logotype} />}
                provider={"facebook"}
              />
              <RegisterButton logo={<GoogleLogo />} provider={"google"} />
              <RegisterButton
                logo={<Apple className={classes.logotype} />}
                provider={"apple"}
              />
            </Box>
            <div className={classes.tosText}>
              <Text align="center" variant="body2">
                {t("tocTextBeforeLink")}
                <Link to="/page/terms">
                  <u>{t("tocLinkText")}</u>
                </Link>
                {t("tocTextAfterLink")}
              </Text>
            </div>
          </>
        ) : (
          !isRegistered && (
            <>
              <Box className={classes.dialogRegisterButton}>
                <RegisterDialogButton user={user} />
              </Box>
              <div className={classes.tosText}>
                <Text align="center" variant="body2">
                  {t("tocTextBeforeLinkKvixTv")}
                  <Link to="/kvix-tv/rules">
                    <u>{t("tocLinkTextKvixTv")}</u>
                  </Link>
                  {t("tocTextAfterLinkKvixTv")}
                </Text>
              </div>
            </>
          )
        )}
      </DialogContent>
    </Dialog>
  );
};
